import React, { useState, useEffect } from 'react';
import { usePageData } from '../../contexts/DataContext';
import { getApiUrl } from '../../utils/environment';
// Import icons from react-icons
import { FaQuestionCircle, FaBug, FaLightbulb, FaAd, FaComments } from 'react-icons/fa';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');
  const { data: contactPage } = usePageData('contactPage');

  // Get CSRF token on component mount
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(';').shift();
        };
        
        let token = getCookie('csrftoken') || getCookie('csrfToken');
        
        if (!token) {
          const response = await fetch(getApiUrl('/csrf/'), {
            method: 'GET',
            credentials: 'include',
          });
          
          if (response.ok) {
            const data = await response.json();
            token = data.csrfToken;
          }
        }
        
        if (token) {
          setCsrfToken(token);
          console.log('? CSRF token acquired:', token);
        }
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
      }
    };
    
    fetchCsrfToken();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (submitStatus.type === 'error') {
      setSubmitStatus({ type: '', message: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setSubmitStatus({ type: 'error', message: 'Please fill in all fields' });
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({ type: 'error', message: 'Please enter a valid email address' });
      return;
    }
    
    if (formData.message.trim().length < 10) {
      setSubmitStatus({ type: 'error', message: 'Message must be at least 10 characters long' });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let tokenToUse = csrfToken;
      if (!tokenToUse) {
        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(';').shift();
        };
        
        tokenToUse = getCookie('csrftoken') || getCookie('csrfToken');
        
        if (!tokenToUse) {
          const csrfResponse = await fetch(getApiUrl('/csrf/'), {
            method: 'GET',
            credentials: 'include',
          });
          
          if (csrfResponse.ok) {
            const csrfData = await csrfResponse.json();
            tokenToUse = csrfData.csrfToken;
            setCsrfToken(tokenToUse);
          }
        }
      }
      
      console.log('?? Submitting contact form with CSRF token:', tokenToUse);
      
      const response = await fetch(getApiUrl('/contact/submit/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': tokenToUse,
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitStatus({ 
          type: 'success', 
          message: data.message || 'Message sent successfully! We will get back to you soon.' 
        });
        
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        
        setTimeout(() => {
          setSubmitStatus({ type: '', message: '' });
        }, 5000);
      } else {
        let errorMessage = 'Failed to send message. Please try again.';
        
        if (data.detail) {
          errorMessage = data.detail;
        } else if (data.name || data.email || data.subject || data.message) {
          errorMessage = Object.values(data).flat().join(', ');
        }
        
        setSubmitStatus({ type: 'error', message: errorMessage });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sidebar items data
  const sidebarItems = [
    {
      icon: <FaQuestionCircle />,
      title: "Support / Help",
      description: "Facing issues with file upload, conversion, or download? Our support team will fix it ASAP."
    },
    {
      icon: <FaBug />,
      title: "Bug Report",
      description: "Found a bug or error? Report it with details or attach a sample file if possible."
    },
    {
      icon: <FaLightbulb />,
      title: "Feature Request",
      description: "Have an idea for a new tool or improvement? Share your suggestion with us."
    },
    {
      icon: <FaAd />,
      title: "Advertising",
      description: "Promote your brand with banner ads, sponsored listings, or custom placements."
    },
    {
      icon: <FaComments />,
      title: "Feedback",
      description: "Share your thoughts about our tools, UI, or overall experience. We'd love to hear!"
    }
  ];

  return (
    <section className="contact-section" style={{ padding: '40px 0' }}>
      <div className="container">
        <h2 style={{ 
          marginBottom: '10px', 
          color: '#333', 
          fontSize: '2rem',
          textAlign: 'center',
	  marginTop: '50px',

        }}>
          {contactPage?.title || 'Contact Us'}
        </h2>
        <p style={{ 
          marginBottom: '40px', 
          color: '#666', 
          fontSize: '1.1rem',
          textAlign: 'center',
          maxWidth: '800px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          {contactPage?.subtitle || "We'd love to hear from you! Reach out for support, partnerships, or just to say hello."}
        </p>

        {/* Main layout wrapper - Changed to grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '50px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          
          {/* Left side - Existing Form (unchanged) */}
          <div>
            <div className="contact-form">
              <h3 style={{ 
                marginBottom: '10px', 
                color: '#333', 
                fontSize: '1.5rem' 
              }}>
                {contactPage?.form_title || 'Send Us a Message'}
              </h3>
              <div className="underline" style={{ 
                height: '3px', 
                width: '60px', 
                backgroundColor: '#007bff', 
                marginBottom: '30px' 
              }}></div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <input 
                  type="text" 
                  name="name" 
                  placeholder="Your Name" 
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required 
                  style={{ 
                    padding: '12px 15px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.3s'
                  }}
                />
                <input 
                  type="email" 
                  name="email" 
                  placeholder="Email" 
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required 
                  style={{ 
                    padding: '12px 15px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.3s'
                  }}
                />
                <select 
                  name="subject" 
                  className="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required
                  style={{ 
                    padding: '12px 15px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem',
                    outline: 'none',
                    backgroundColor: 'white',
                    transition: 'border-color 0.3s'
                  }}
                >
                  <option value="">Subject</option>
                  <option value="support">Support</option>
                  <option value="partnership">Partnership</option>
                  <option value="feedback">Feedback</option>
                  <option value="other">Other</option>
                </select>
                <textarea 
                  name="message" 
                  placeholder="Your Message" 
                  rows="6"
                  value={formData.message}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required
                  style={{ 
                    padding: '12px 15px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: '150px',
                    transition: 'border-color 0.3s'
                  }}
                ></textarea>
                
                {submitStatus.message && (
                  <div className={`form-message ${submitStatus.type}`} style={{ 
                    padding: '12px 15px',
                    borderRadius: '8px',
                    backgroundColor: submitStatus.type === 'success' ? '#d4edda' : '#f8d7da',
                    color: submitStatus.type === 'success' ? '#155724' : '#721c24',
                    border: `1px solid ${submitStatus.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                    fontSize: '0.95rem'
                  }}>
                    {submitStatus.message}
                  </div>
                )}
                
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  style={{ 
                    padding: '14px 30px',
                    borderRadius: '8px',
                    backgroundColor: isSubmitting ? '#6c757d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.3s',
                    alignSelf: 'flex-start'
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>

          {/* Right side - New Sidebar */}
          <div>
        <h3
  style={{
    marginBottom: '20px',
    color: '#333',
    fontSize: '1.8rem',
    textAlign: 'left',  
  }}
>
              How Can We Help?
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px',  textAlign: 'left', }}>
              {sidebarItems.map((item, index) => (
                <div 
                  key={index}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '10px',
                    padding: '18px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
                    border: '1px solid #e9ecef',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    ':hover': {
                      backgroundColor: '#f8f9fa',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.08)'
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.03)';
                  }}
                >
                  <div style={{ 
                    color: '#007bff',
                    fontSize: '20px',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}>
                    {item.icon}
                  </div>
                  <div>
                    <h4 style={{ 
                      margin: '0 0 6px 0', 
                      color: '#333', 
                      fontSize: '1.05rem',
                      fontWeight: '600'
                    }}>
                      {item.title}
                    </h4>
                    <p style={{ 
                      margin: 0, 
                      color: '#666', 
                      fontSize: '0.9rem',
                      lineHeight: '1.5'
                    }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
           
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;